/**
 * Shared CSV helpers for PO asset imports.
 * Supports comma/semicolon delimiters and quoted fields (including "" escapes).
 */

export type CsvDelimiter = "," | ";";

/** Count delimiters outside quotes to pick , vs ; (Excel regional CSVs often use ;). */
export function detectCsvDelimiter(headerLine: string): CsvDelimiter {
  let inQuotes = false;
  let commas = 0;
  let semis = 0;

  for (let i = 0; i < headerLine.length; i++) {
    const ch = headerLine[i];
    if (ch === '"') {
      if (inQuotes && headerLine[i + 1] === '"') {
        i++;
        continue;
      }
      inQuotes = !inQuotes;
      continue;
    }
    if (inQuotes) continue;
    if (ch === ",") commas++;
    if (ch === ";") semis++;
  }

  return semis > commas ? ";" : ",";
}

/**
 * Split one CSV row into fields.
 * - Honors "quoted" sections so commas/semicolons inside quotes stay in the field
 * - Supports Excel-style escaped quotes ("")
 */
export function parseCSVRow(
  row: string,
  delimiter: CsvDelimiter = ","
): string[] {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < row.length; i++) {
    const ch = row[i];

    if (ch === '"') {
      if (inQuotes && row[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (ch === delimiter && !inQuotes) {
      values.push(current.trim());
      current = "";
      continue;
    }

    current += ch;
  }

  values.push(current.trim());
  return values;
}

/** Normalize price/qty text: remove currency, spaces, thousand separators. */
export function normalizeCsvNumericString(value: string): string {
  let s = (value ?? "")
    .trim()
    .replace(/[₹$€£]/g, "")
    .replace(/\s/g, "");

  if (!s) return "";

  // European: 1.234,56 or 12,5 → use comma as decimal
  if (/^\d{1,3}(\.\d{3})+(,\d+)?$/.test(s) || /^\d+,\d+$/.test(s)) {
    s = s.replace(/\./g, "").replace(",", ".");
    return s;
  }

  // US/IN: 1,234.56 or 1,234 → strip thousand commas
  if (s.includes(",")) {
    s = s.replace(/,/g, "");
  }

  return s;
}

export function parseCsvNumber(value: string, fallback = 0): number {
  const normalized = normalizeCsvNumericString(value);
  if (!normalized || normalized === "." || normalized === "-") return fallback;
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Split CSV into logical rows, keeping newlines that sit inside quoted fields.
 */
export function splitCsvLines(csvContent: string): string[] {
  const text = csvContent.replace(/^\uFEFF/, "");
  const lines: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') {
        current += '""';
        i++;
      } else {
        inQuotes = !inQuotes;
        current += ch;
      }
      continue;
    }

    if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      const trimmed = current.trim();
      // Keep header even if empty; skip blank data rows later
      if (trimmed.length > 0 || lines.length === 0) {
        lines.push(trimmed);
      }
      current = "";
      continue;
    }

    current += ch;
  }

  const trimmed = current.trim();
  if (trimmed.length > 0 || lines.length === 0) {
    lines.push(trimmed);
  }

  return lines;
}
